<template>
    <div data-testid="approvals-report" class="all-height all-width">
        <div class="pa-6 pb-16 d-flex flex-column gap-6">

            <ReportHeader 
            title="Approvals Report" 
            test-id-prefix="approvals-report" 
            @refresh="fetchInitialData" />

            <div class="d-flex flex-column gap-6">
                <ReportFilter 
                apply-button-text="Generate" 
                cycletype="Actual" 
                :showrequesttype="true"
                :showmultirequesttype="true"
                :showmodule="true" 
                :showSync="false" 
                :showFavourite="false" 
                :showrole="false"
                :showperiod="false"
                :shownetwork="false" 
                :showregion="false" 
                :showcountry="false"
                :showreportingunit="false" 
                :showrutype="false"
                :showapprovalstatus="true" 
                :showmultiapprovalstatus="true" 
                :showReset="true" 
                @newdata="handleFilterData" />
            </div>
            
            <ReportTable 
            :loading="getApprovalsReportListLoading" 
            :headers="getApprovalsReportHeader"
            :items="formattedApprovalsData" 
            :total-pages="getApprovalsReportTotalPages"
            :current-page="currentPage" 
            :items-per-page="itemsPerPage" 
            test-id-prefix="approvals-report"
            :shimmer-slots="shimmerSlots"
            :data-slots="dataSlots" 
            @page-change="handlePageChange"
            @download="downloadFile" 
            @view-details="showDetails"
            @filter-change="handleTableFilterChange">
             <template v-slot:indexno="{ item }">
                    <div>
                        {{ item.serial_number }}
                    </div>
                </template>
             <template v-slot:filter="{ item }">
                    <div>
                     {{ formatRequestTypes(item.filter.request_type) }}
                    </div>
                </template>
            </ReportTable>
        </div>

        <ReportDialog 
        v-model="detailsDialog" 
        v-if="detailsDialog && detailsItem" 
        title="Approvals Report"
        :details-item="detailsItem" test-id-prefix="approvals-report-dialog">
            <template v-slot:header-fields="{ item }">
                <v-row no-gutters class="mb-2 align-center">
                    <v-col cols="12" sm="4" class="details-label">Approval Scope</v-col>
                    <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                        {{  formatRequestTypes(item.filter.request_type) }}
                    </v-col>
                </v-row>
            </template>

            <template v-slot:middle-fields="{ item }">
                <v-row no-gutters class="mb-2 align-center">
                    <v-col cols="12" sm="4" class="details-label">Module</v-col>
                    <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                        {{ getModuleLabel(item.module) }}
                    </v-col>
                </v-row>
            </template>
        </ReportDialog>
        
    </div>
</template>
<script>
import ReportHeader from './components/ReportHeader.vue';
import ReportTable from './components/ReportTable.vue';
import ReportDialog from './components/ReportDialog.vue';
import { STATUS_VARIANTS } from '@/modules/varianceanalysis/utils/statusConstants.js';
import { PAGINATION, REPORTS_API, MONTHS, FILTER_REQUEST_TYPE_LIST, MODULE_LABELS } from '@/modules/varianceanalysis/utils/constants.js';
import { mapActions, mapGetters } from 'vuex';
import ReportFilter from '../../views/ReportFilter.vue';

export default {
    name: 'ApprovalsReport',

    components: {
        ReportFilter,
        ReportHeader,
        ReportTable,
        ReportDialog
    },

    data() {
        return {
            selectedFilter: {},
            cycletype: 'Actual',
            filterLoading: false,
            filterList: {},
            detailsDialog: false,
            detailsItem: null,
            fetchTimeout: null,
            isInitialLoad: true, // Track if this is the first load
            currentPage: 1,
            itemsPerPage: PAGINATION.DEFAULT_LIMIT,
            statusFilter: [],
            dataSlots: ['indexno', 'filter','created_at', 'status', 'action'],
            shimmerSlots: ['indexno', 'filter', 'created_at', 'status', 'action'],
        };
    },

    mounted() {
        // Initial data fetch is deferred to handleFilterData when ReportFilter
        // signals loading complete (key='loading', value=false).
        // This prevents duplicate API calls from DataTableNew's created() hook
        // and mounted() both triggering fetches independently.
    },

    computed: {
        ...mapGetters('varianceanalysis/reports', [
            'getApprovalsReportData',
            'getApprovalsReportListLoading',
            'getApprovalsReportFilterLoading',
            'getApprovalsReportHeader',
            'getApprovalsReportTotalPages',
        ]),

        /**
         * Generates shimmer/skeleton placeholder items for loading state
         * @returns {Array} Array of placeholder items for skeleton loader display
         */
        shimmerItems() {
            return Array.from({ length: 5 }, (_, index) => ({
                __key: index,
                __formatedkey: index,
                _id: index,
                indexno: '',
                period_detail: '',
                created_at: '',
                status: '',
                action: '',
            }));
        },

        /**
         * Reactively formats approvals report data for display in the data table
         * @returns {Array} Formatted array of approvals report items
         */
        formattedApprovalsData() {
            return this.getApprovalsReportData.map((item, index) => ({
                __key: index,
                __formatedkey: index,
                _id: item._id,
                indexno: (this.currentPage - 1) * this.itemsPerPage + index + 1,
                serial_number: (this.currentPage - 1) * this.itemsPerPage + index + 1,
                ru_codes: item.ru_codes,
                period: item.period,
                errormessage: item.errormessage,
                stage: item.stage,
                status: this.getStages(item.stage),
                created_at: item.created_at,
                created_by: item.created_by,
                user_detail: item.user_detail,
                country_detail: item.country_details,
                region_detail: item.region_details,
                period_detail: item.period_detail,
                reportingunit_detail: item.reportingunit_details,
                network_detail: item.network_details,
                filter: item.filter,
                module: item.module,
            }));
        },
    },


    beforeDestroy() {
        if (this.fetchTimeout) clearTimeout(this.fetchTimeout);
        // Reset component data to prevent stale state
        this.selectedFilter = {};
        this.cycletype = 'Actual';
    },

    methods: {
        /**
         * Handles page change from Pagination component - fetches data for new page
         * @param {Number} page - New page number (1-based from Pagination component)
         */
        handlePageChange(page) {
            this.currentPage = page; // Update current page state for UI
            this.fetchPageData(page - 1); // API uses 0-based page index
        },

        /**
         * Handles status filter changes from DataTableNew column filter
         * @param {Object} filters - Filter object e.g. { status: ['ready', 'failed'] }
         */
        handleTableFilterChange(filters) {
            if (this.isInitialLoad) return; // Skip filter events during initial load
            this.statusFilter = filters.status || [];
            this.currentPage = 1;
            this.fetchPageData(0);
        },

        /**
         * Fetches data for a specific page
         * @param {Number} page - Page number to fetch
         */
        async fetchPageData(page) {
            try {
                await this.fetchApprovalsReportList({
                    page: page,
                    limit: this.itemsPerPage,
                    cycletype: this.cycletype,
                    type: 'allapprovals_' + this.cycletype.toLowerCase(),
                    ...(this.statusFilter.length ? { status: this.statusFilter } : {})
                });
            } catch (error) {
                console.error('[ApprovalsReport]: Error fetching page data', error);
            }
        },
        getMonthName(monthNumber) {
            if (!monthNumber) return '';
            return MONTHS[monthNumber - 1] || monthNumber;
        },
        downloadFile(id) {
            this.$nova.downloadFile(undefined, {}, REPORTS_API.DOWNLOAD + id)
        },
        showDetails(item) {
            this.detailsDialog = true;
            this.detailsItem = item;
        },

        /**
         * Maps stage codes to their corresponding status variant constants
         * @param {Number} stage - The stage code (0=Generating, 1=Ready, 4=Generating, others=Failed)
         * @returns {String} The stage status variant constant
         */
        getStages(stage) {
            switch (stage) {
                case 0:
                    return STATUS_VARIANTS.GENERATING;
                case 1:
                    return STATUS_VARIANTS.READY;
                case 4:
                    return STATUS_VARIANTS.GENERATING;
                case 2:
                    return STATUS_VARIANTS.FAILED;
                default:
                    return STATUS_VARIANTS.FAILED;
            }
        },
        ...mapActions('varianceanalysis/reports', [
            'fetchApprovalsReportList',
            'fetchApprovalsReportFiltered'
        ]),

        /**
         * Handles filter data changes from GlobalFilter component
         * @param {Object} filterData - Filter data with key-value pairs
         */
        handleFilterData(filterData) {
            // Always update selectedFilter first with incoming data
            if (filterData.key && filterData.value !== undefined) {
                this.$set(this.selectedFilter, filterData.key, filterData.value);
            }

            // Handle initial filter load completion - fetch initial data once filters are ready
            if (filterData.key === 'loading' && filterData.value === false && this.isInitialLoad) {
                this.isInitialLoad = false;
                this.fetchInitialData();
                return;
            }

            // Only fetch filtered data when reload is triggered (Apply button clicked)
            if (filterData.reload) {
                if (this.fetchTimeout) clearTimeout(this.fetchTimeout);

                this.fetchTimeout = setTimeout(() => {
                    this.fetchFilteredData();
                }, 100);
            }
        },

        /**
         * Fetches initial paginated data on component mount
         * API: /v3/varianceanalysis/reportingunit/downloadallapprovalsllist
         */
        async fetchInitialData() {
            try {
                this.currentPage = 1; // Reset to first page (1-based for UI)
                this.statusFilter = []; // Reset status filter
                await this.fetchApprovalsReportList({ 
                    page: 0, 
                    limit: this.itemsPerPage, 
                    cycletype: this.cycletype, 
                    type: 'allapprovals_' + this.cycletype.toLowerCase() 
                });
            } catch (error) {
                console.error('[ApprovalsReport]: Error fetching initial data', error);
            }
        },

        /**
         * Fetches filtered approvals report data
         * API: /v3/varianceanalysis/analysis/downloadapprovalreport
         */
        async fetchFilteredData() {
            console.log('[ApprovalsReport]: selectedFilter', this.selectedFilter);

            try {
                this.filterLoading = true;
                this.currentPage = 1; // Reset to first page on filter apply
                const payload = {
                    request_type: this.transformToArray(this.selectedFilter.selectedrequesttype),
                    module: this.transformFilterValue(this.selectedFilter.selectedmodule),
                    status:this.transformToArray(this.selectedFilter.selectedapprovalstatus),
                };
                console.log(payload,"payload");
                
                await this.fetchApprovalsReportFiltered(payload);
                await this.fetchInitialData();
            } catch (error) {
                console.error('[ApprovalsReport]: Error fetching filtered data', error);
            } finally {
                this.filterLoading = false;
            }
        },

        /**
         * Transforms filter value: returns "all" if empty/null, otherwise returns the value
         * @param {String|null} value - Filter value
         * @returns {String} Transformed value
         */
        transformFilterValue(value) {
            return value || 'all';
        },

        /**
         * Transforms filter value to array: returns "all" if empty/null, otherwise returns array
         * Used for multi-select fields like network, region, country
         * @param {String|Array|null} value - Filter value
         * @returns {String|Array} Transformed value as array or 'all'
         */
        transformToArray(value) {
            if (!value || value === 'all' || (Array.isArray(value) && value.length === 0)) {
                return 'all';
            }
            return Array.isArray(value) ? value : [value];
        },

        /**
         * Transforms reporting unit value: returns "all" if empty or 'all' string, otherwise returns array
         * @param {String|Array|null} value - Reporting unit value
         * @returns {String|Array} Transformed value
         */
        transformReportingUnit(value) {
            if (!value || value === 'all' || (Array.isArray(value) && value.length === 0)) {
                return 'all';
            }
            return Array.isArray(value) ? value : [value];
        },

        /**
         * Formats request type IDs to their display names
         * @param {String|Array} requestTypes - Single ID or array of request type IDs
         * @returns {String} Comma-separated display names
         */
        formatRequestTypes(requestTypes) {
            if (!requestTypes) return '';
            const types = Array.isArray(requestTypes) ? requestTypes : [requestTypes];
            return types
                .map(typeId => FILTER_REQUEST_TYPE_LIST[typeId]?.name || typeId)
                .join(', ');
        },

         /**
         * Gets the display label for a module type
         * @param {String} module - Module code (pl, bs, all)
         * @returns {String} Display label
         */
        getModuleLabel(module) {
            return MODULE_LABELS[module] || MODULE_LABELS.all;
        },
    },
};
</script>

<style scoped>
.failed-reason {
    color: var(--status-red);
}
</style>
